import { GlbModelsMap } from "@/contexts/glb-models-context";
import { Camera, Group, Scene, WebGLRenderer } from "three";
import { AbstractModel } from "./AbstractModel";
import type { VisualSettingsSource } from "./VisualSettings";
import type { SceneRevealSource } from "./SceneReveal";
import type { PollutionEffectConfig } from "./PollutionEffects";

export type AmbientFishConfig = {
  models: {
    model: AbstractModel;
    targetSize: number;
    rotation: { x: number; y: number; z: number };
  }[];
  visible: boolean;
  count: number;
  // Множитель скорости движения по орбите (1 = базовая). Меньше — медленнее.
  speedScale?: number;
  // Сдвиг центра орбиты по глубине (Z). Отрицательное значение отодвигает дальше от камеры.
  depthOffset?: number;
  // Множитель скорости проигрывания анимации модели (1 = базовая).
  animationTimeScale?: number;
  // Режим зацикливания клипа: "repeat" (по умолчанию) или "pingpong" (вперёд-назад,
  // для незамкнутых клипов — без рывка в точке стыка).
  animationLoop?: "repeat" | "pingpong";
  pollutionCounts?: {
    pollutedCount: number;
    cleanCount: number;
  };
  pollutionShoals?: {
    pollutedCount: number;
    cleanCount: number;
  };
  shoals?: {
    count: number;
    fishPerShoal: number;
    fishCounts?: number[];
    spread: { x: number; y: number; z: number };
  };
};

export type EnvironmentDecorItemConfig = {
  model: AbstractModel;
  targetSize: number;
  position: { x: number; y: number; z: number };
  rotation: { x: number; y: number; z: number };
  scale?: { x: number; y: number; z: number };
};

export function normalizeAmbientFishConfigs(
  ambientFish: AmbientFishConfig | AmbientFishConfig[] | undefined,
): AmbientFishConfig[] {
  if (!ambientFish) return [];
  return Array.isArray(ambientFish) ? ambientFish : [ambientFish];
}

export type EnvironmentDecorSource = {
  get: () => EnvironmentDecorItemConfig[];
};

export type CleanerViewSettings = {
  offset: { x: number; y: number; z: number };
  rotationDeg: { x: number; y: number; z: number };
};

export type CleanerViewSource = {
  get: () => CleanerViewSettings;
};

export interface SceneProps {
  onWin: () => void;
  scene: Scene | Group;
  rootScene?: Scene;
  renderer: WebGLRenderer;
  canvas: HTMLCanvasElement;
  camera: Camera;
  models: GlbModelsMap;
  weaponModel: AbstractModel;
  garbageModels: readonly AbstractModel[];
  ambientFish?: AmbientFishConfig | AmbientFishConfig[];
  environmentDecor?: EnvironmentDecorItemConfig[];
  environmentDecorSource?: EnvironmentDecorSource;
  pollutionEffect?: PollutionEffectConfig;
  cleanerView?: CleanerViewSource;
  startPoint: { x: number; y: number; z: number };
  visualSettings?: VisualSettingsSource;
  sceneReveal?: SceneRevealSource;
  garbageSpawnConfig?: {
    minRadius?: number;
    maxRadius?: number;
    maxCameraYOffset?: number;
  };
}
